import React, { useRef, useState, useMemo, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';
import { Row, Col, ModalBody } from 'reactstrap';
import {
  differenceInMinutes,
  format,
  set,
  startOfDay,
  endOfDay,
  subHours,
} from 'date-fns';
import { FiX } from 'react-icons/fi';
import {
  Button,
  Switch,
  Checkbox,
  Input,
  Select,
  SingleCalendar,
  RadioGroup,
} from 'takeat-design-system-ui-kit';
import getValidationErrors from '~/services/getValidationErrors';
import api2 from '~/services/api2';
import {
  Header,
  Container,
  StyledForm,
  Title,
  InputArea,
  HourRange,
} from './styles';

function NewTicket({ getTickets, toggleAddTicketModal }) {
  const formRef = useRef(null);
  const [discountType, setDiscountType] = useState('percentage');
  const [type, setType] = useState('Desconto (%)*');
  const today = useMemo(() => {
    return format(startOfDay(subHours(new Date(), 3)), 'yyyy-MM-dd');
  }, []);
  const [expirationDate, setExpirationDate] = useState(null);

  const [codeError, setCodeError] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [discountError, setDiscountError] = useState(null);

  function changeType(new_discount_type) {
    switch (new_discount_type) {
      case 'percentage':
        setType('Desconto (%)*');
        break;
      case 'absolute':
        setType('Desconto (R$)*');
        break;
      default:
        break;
    }

    setDiscountType(new_discount_type);
  }

  const [active, setActive] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [expires, setExpires] = useState(false);
  const [limitClientCouponUse, setLimitClientCouponUse] = useState(true);
  const [limitCoupon, setLimitCoupon] = useState(true);
  const [data, setData] = useState({
    code: '',
    minimum_price: '',
    coupon_code: '',
    maximum_discount: '',
    expires_at: endOfDay(new Date()),
    userLimit: '',
    discount: '',
    distance_limit: '',
    limit_amount: '',
    accepted_channels: ['delivery'],
    start_time: null,
    end_time: null,
    limit_hour: false,
  });
  const [ticketLimit, setTicketLimit] = useState(null);

  const OPERATION_OPTIONS = [
    {
      label: 'Delivery',
      value: 'delivery',
    },
    {
      label: 'Presencial',
      value: 'presencial',
    },
  ];

  const couponType = [
    { label: 'Reais (R$)', value: 'absolute' },
    { label: 'Porcentagem (%)', value: 'percentage' },
    { label: 'Frete Grátis', value: 'free-shipping' },
  ];

  const HOUR_OPTIONS = [
    {
      label: '00:00',
      value: set(new Date(), { hours: 0, minutes: 0, seconds: 0 }),
    },
    {
      label: '00:30',
      value: set(new Date(), { hours: 0, minutes: 30, seconds: 0 }),
    },
    {
      label: '01:00',
      value: set(new Date(), { hours: 1, minutes: 0, seconds: 0 }),
    },
    {
      label: '01:30',
      value: set(new Date(), { hours: 1, minutes: 30, seconds: 0 }),
    },
    {
      label: '02:00',
      value: set(new Date(), { hours: 2, minutes: 0, seconds: 0 }),
    },
    {
      label: '02:30',
      value: set(new Date(), { hours: 2, minutes: 30, seconds: 0 }),
    },
    {
      label: '03:00',
      value: set(new Date(), { hours: 3, minutes: 0, seconds: 0 }),
    },
    {
      label: '03:30',
      value: set(new Date(), { hours: 3, minutes: 30, seconds: 0 }),
    },
    {
      label: '04:00',
      value: set(new Date(), { hours: 4, minutes: 0, seconds: 0 }),
    },
    {
      label: '04:30',
      value: set(new Date(), { hours: 4, minutes: 30, seconds: 0 }),
    },
    {
      label: '05:00',
      value: set(new Date(), { hours: 5, minutes: 0, seconds: 0 }),
    },
    {
      label: '05:30',
      value: set(new Date(), { hours: 5, minutes: 30, seconds: 0 }),
    },
    {
      label: '06:00',
      value: set(new Date(), { hours: 6, minutes: 0, seconds: 0 }),
    },
    {
      label: '06:30',
      value: set(new Date(), { hours: 6, minutes: 30, seconds: 0 }),
    },
    {
      label: '07:00',
      value: set(new Date(), { hours: 7, minutes: 0, seconds: 0 }),
    },
    {
      label: '07:30',
      value: set(new Date(), { hours: 7, minutes: 30, seconds: 0 }),
    },
    {
      label: '08:00',
      value: set(new Date(), { hours: 8, minutes: 0, seconds: 0 }),
    },
    {
      label: '08:30',
      value: set(new Date(), { hours: 8, minutes: 30, seconds: 0 }),
    },
    {
      label: '09:00',
      value: set(new Date(), { hours: 9, minutes: 0, seconds: 0 }),
    },
    {
      label: '09:30',
      value: set(new Date(), { hours: 9, minutes: 30, seconds: 0 }),
    },
    {
      label: '10:00',
      value: set(new Date(), { hours: 10, minutes: 0, seconds: 0 }),
    },
    {
      label: '10:30',
      value: set(new Date(), { hours: 10, minutes: 30, seconds: 0 }),
    },
    {
      label: '11:00',
      value: set(new Date(), { hours: 11, minutes: 0, seconds: 0 }),
    },
    {
      label: '11:30',
      value: set(new Date(), { hours: 11, minutes: 30, seconds: 0 }),
    },
    {
      label: '12:00',
      value: set(new Date(), { hours: 12, minutes: 0, seconds: 0 }),
    },
    {
      label: '12:30',
      value: set(new Date(), { hours: 12, minutes: 30, seconds: 0 }),
    },
    {
      label: '13:00',
      value: set(new Date(), { hours: 13, minutes: 0, seconds: 0 }),
    },
    {
      label: '13:30',
      value: set(new Date(), { hours: 13, minutes: 30, seconds: 0 }),
    },
    {
      label: '14:00',
      value: set(new Date(), { hours: 14, minutes: 0, seconds: 0 }),
    },
    {
      label: '14:30',
      value: set(new Date(), { hours: 14, minutes: 30, seconds: 0 }),
    },
    {
      label: '15:00',
      value: set(new Date(), { hours: 15, minutes: 0, seconds: 0 }),
    },
    {
      label: '15:30',
      value: set(new Date(), { hours: 15, minutes: 30, seconds: 0 }),
    },
    {
      label: '16:00',
      value: set(new Date(), { hours: 16, minutes: 0, seconds: 0 }),
    },
    {
      label: '16:30',
      value: set(new Date(), { hours: 16, minutes: 30, seconds: 0 }),
    },
    {
      label: '17:00',
      value: set(new Date(), { hours: 17, minutes: 0, seconds: 0 }),
    },
    {
      label: '17:30',
      value: set(new Date(), { hours: 17, minutes: 30, seconds: 0 }),
    },
    {
      label: '18:00',
      value: set(new Date(), { hours: 18, minutes: 0, seconds: 0 }),
    },
    {
      label: '18:30',
      value: set(new Date(), { hours: 18, minutes: 30, seconds: 0 }),
    },
    {
      label: '19:00',
      value: set(new Date(), { hours: 19, minutes: 0, seconds: 0 }),
    },
    {
      label: '19:30',
      value: set(new Date(), { hours: 19, minutes: 30, seconds: 0 }),
    },
    {
      label: '20:00',
      value: set(new Date(), { hours: 20, minutes: 0, seconds: 0 }),
    },
    {
      label: '20:30',
      value: set(new Date(), { hours: 20, minutes: 30, seconds: 0 }),
    },
    {
      label: '21:00',
      value: set(new Date(), { hours: 21, minutes: 0, seconds: 0 }),
    },
    {
      label: '21:30',
      value: set(new Date(), { hours: 21, minutes: 30, seconds: 0 }),
    },
    {
      label: '22:00',
      value: set(new Date(), { hours: 22, minutes: 0, seconds: 0 }),
    },
    {
      label: '22:30',
      value: set(new Date(), { hours: 22, minutes: 30, seconds: 0 }),
    },
    {
      label: '23:00',
      value: set(new Date(), { hours: 23, minutes: 0, seconds: 0 }),
    },
    {
      label: '23:30',
      value: set(new Date(), { hours: 23, minutes: 30, seconds: 0 }),
    },
  ];

  const datesHaveSameSchedule = (date1, date2) => {
    return (
      date1.getHours() === date2.getHours() &&
      date1.getMinutes() === date2.getMinutes()
    );
  };

  function changeActive() {
    setActive(!active);
  }

  function changeIsPublic() {
    setIsPublic(!isPublic);
  }

  function changeLimitCoupon() {
    setLimitCoupon(!limitCoupon);
  }

  function changeClienteCouponUse() {
    setLimitClientCouponUse(!limitClientCouponUse);
  }

  function changeExpires() {
    setExpires(!expires);
    !expires ? setExpirationDate(`${today}T03:00:00`) : setExpirationDate(null);
    setData((prevData) => ({
      ...prevData,
      expires_at: null,
    }));
  }

  async function handleSubmit() {
    try {
      if (formRef?.current) {
        formRef.current.setErrors({});
      }

      const schema = Yup.object().shape({
        code: Yup.string()
          .min(5, 'O nome deve ter no mínimo 5 caracteres')
          .required('O nome é obrigatório e deve ter no mínimo 5 caracteres'),
        coupon_code: Yup.string().required('O código é obrigatório'),
        discount:
          discountType !== 'free-shipping'
            ? Yup.string().required('O desconto é obrigatório')
            : Yup.string(),
        maximum_discount:
          discountType !== 'free-shipping'
            ? Yup.string()
                .transform((value, originalValue) =>
                  originalValue === '' ? null : value
                )
                .nullable()
                .min(1, 'O desconto deve ser maior que 1 Real')
            : Yup.string(),
        accepted_channels: Yup.array()
          .of(Yup.string().oneOf(['delivery', 'presencial']))
          .required(),
      });

      await schema.validate(data, {
        abortEarly: false,
      });
      let formattedPrice = 0;
      let distance_limit = null;
      if (discountType === 'free-shipping') {
        data.distance_limit = data.distance_limit.trim();
        if (data.distance_limit) {
          distance_limit = parseFloat(data.distance_limit.replace(',', '.'));
        }
      } else if (discountType === 'percentage') {
        formattedPrice = data.discount.includes(',')
          ? parseFloat(data.discount.replace(',', '.')) / 100
          : parseFloat(data.discount) / 100;
      } else {
        formattedPrice = data.discount;
      }

      const dataToSend = {
        currency: 'BRL',
        name: data.code,
        code: data.coupon_code,
        discount_type: discountType,
        discount: formattedPrice,
        limit_date: data.expires_at,
        is_active: active,
        is_public: isPublic,
        description: '',
        title: '',
        distance_limit: data.distance_limit === '' ? 0 : data.distance_limit,
        accepted_channels: data.accepted_channels,
        limit_hour: data.limit_hour,
      };
      if (data.minimum_price !== '') {
        dataToSend.minimum_price = data.minimum_price.toFixed(2);
      }
      if (data.maximum_discount !== '') {
        dataToSend.maximum_discount = parseFloat(
          data.maximum_discount.includes(',')
            ? data.maximum_discount.replace(',', '.')
            : data.maximum_discount
        ).toFixed(2);
      }
      if (data.limit_amount !== '') {
        dataToSend.limit_amount = data.limit_amount;
      }
      if (data.userLimit !== '') {
        dataToSend.buyer_limit_buy = data.userLimit;
      }
      if (data.start_time) {
        dataToSend.start_time = data.start_time;
      }

      if (data.end_time) {
        dataToSend.end_time = data.end_time;
      }
      await api2.post('/restaurants/discount-coupons', dataToSend);

      toast.success('Cupom cadastrado com sucesso!');
      getTickets();
      toggleAddTicketModal();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        toast.error('Erro ao cadastrar cupom!');
        const errors = getValidationErrors(error);
        if (errors.coupon_code) {
          setCodeError(errors.coupon_code);
        } else {
          setCodeError(null);
        }
        if (errors.code) {
          setNameError(errors.code);
        } else {
          setNameError(null);
        }
        if (errors.discount) {
          setDiscountError(errors.discount);
        } else {
          setDiscountError(null);
        }
        if (formRef?.current) {
          formRef.current.setErrors(errors);
        }
      } else if (error.response?.data?.errorType === 'coupon_already_exists') {
        toast.error('Já existe um cupom cadastrado com esse código.');
      } else {
        toast.error('Erro ao cadastrar cupom!');
      }
    }
  }

  const modalRef = useRef(null);

  return (
    <ModalBody ref={modalRef}>
      <Header>
        <Title>Cadastrar cupons</Title>

        <FiX
          size={22}
          color="#222222"
          cursor="pointer"
          strokeWidth={3}
          onClick={() => toggleAddTicketModal()}
        />
      </Header>
      <Container>
        <Row>
          <Col>
            <StyledForm ref={formRef} onSubmit={handleSubmit}>
              <Row style={{ paddingBottom: 16 }}>
                <RadioGroup
                  options={couponType}
                  name="discount_type"
                  onChange={(e) => changeType(e.target.value)}
                  value={discountType}
                />
              </Row>

              <InputArea>
                {discountType !== 'free-shipping' ? (
                  <Input
                    label={type}
                    name="discount"
                    error={discountError}
                    type="text"
                    containerStyles={{ gridArea: 'discount' }}
                    isCurrency={discountType === 'absolute'}
                    hasPrefix={discountType === 'absolute'}
                    onChange={(e) => {
                      const value = e.target?.value;
                      setData((prevData) => ({
                        ...prevData,
                        discount: value,
                      }));
                    }}
                  />
                ) : (
                  <Input
                    label="Limite de Distância (km)"
                    name="distance_limit"
                    type="text"
                    containerStyles={{ gridArea: 'discount' }}
                    onChange={(e) => {
                      e.persist();
                      setData((prevData) => ({
                        ...prevData,
                        distance_limit: e.target.value,
                      }));
                    }}
                  />
                )}

                <Input
                  label="Valor Mínimo (R$)"
                  name="minimum_price"
                  isCurrency
                  hasPrefix
                  containerStyles={{ gridArea: 'minimum_price' }}
                  value={data.minimum_price}
                  onChange={(e) => {
                    const value = e.target?.value;
                    setData((newdata) => ({
                      ...newdata,
                      minimum_price: value,
                    }));
                  }}
                />

                <Input
                  label="Nome do Cupom*"
                  name="name"
                  error={nameError}
                  type="text"
                  placeholder="Ex.: 60% OFF"
                  containerStyles={{ gridArea: 'coupon_name' }}
                  value={data.code}
                  onChange={(e) => {
                    e.persist();
                    setData((newdata) => ({
                      ...newdata,
                      code: e.target?.value,
                    }));
                  }}
                />

                <Input
                  label="Código do cupom*"
                  name="code"
                  error={codeError}
                  type="text"
                  placeholder="Ex.: 60OFF"
                  containerStyles={{ gridArea: 'coupon_code' }}
                  value={data.coupon_code}
                  onChange={(e) => {
                    e.persist();
                    setData((newdata) => ({
                      ...newdata,
                      coupon_code: e.target?.value?.toUpperCase(),
                    }));
                  }}
                />

                <Input
                  label="Desconto Máximo (R$)"
                  name="maximum_discount"
                  type="text"
                  disabled={
                    discountType === 'free-shipping' ||
                    discountType === 'absolute'
                  }
                  placeholder=""
                  containerStyles={{ gridArea: 'maximum_discount' }}
                  value={data.maximum_discount}
                  onChange={(e) => {
                    e.persist();
                    setData((newdata) => ({
                      ...newdata,
                      maximum_discount: e.target?.value,
                    }));
                  }}
                />

                <div
                  style={{
                    gridArea: 'date',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <span
                    style={{
                      fontWeight: '600',
                      color: '#545454',
                      height: 28,
                      fontSize: 16,
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  >
                    Data Final
                  </span>
                  <SingleCalendar
                    containerStyles={{ width: '100%', fontWeight: 700 }}
                    disabled={expires}
                    onSelectDate={(e) => {
                      setData((prevData) => ({
                        ...prevData,
                        expires_at: endOfDay(e),
                      }));
                    }}
                    onlyDate
                    style={{ color: 'yellow !important' }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      paddingBottom: 6,
                      paddingTop: 6,
                    }}
                  >
                    <Checkbox
                      label="Sem data de expiração"
                      onChange={changeExpires}
                    />
                  </div>
                </div>

                <Select
                  label="Operação*"
                  isMulti
                  options={OPERATION_OPTIONS}
                  value={OPERATION_OPTIONS.filter((option) =>
                    data.accepted_channels.includes(option.value)
                  )}
                  containerStyles={{ gridArea: 'operation' }}
                  onChange={(e) => {
                    setData((prevData) => ({
                      ...prevData,
                      accepted_channels: e.map((item) => item.value),
                    }));
                  }}
                />
                {console.log(data)}

                <HourRange style={{ gridArea: 'hourRange' }}>
                  <label>Cupom ativo entre</label>
                  <div className="selects_container">
                    <Select
                      menuPortalTarget={modalRef?.current}
                      isDisabled={data.limit_hour}
                      value={
                        HOUR_OPTIONS.find((option) => {
                          if (!data.start_time) {
                            return null;
                          }
                          return datesHaveSameSchedule(
                            option.value,
                            new Date(data.start_time)
                          );
                        }) || null
                      }
                      options={HOUR_OPTIONS.map((option) => {
                        if (!data.end_time) {
                          return option;
                        }
                        const diff = differenceInMinutes(
                          data.end_time,
                          option.value
                        );
                        return { ...option, isDisabled: diff <= 0 };
                      })}
                      onChange={(e) => {
                        if (!e) return;
                        setData((state) => ({
                          ...state,
                          start_time: e.value,
                        }));
                      }}
                    />
                    <span>até</span>
                    <Select
                      menuPortalTarget={modalRef?.current}
                      isDisabled={data.limit_hour}
                      value={
                        HOUR_OPTIONS.find((option) => {
                          if (!data.end_time) {
                            return null;
                          }
                          return datesHaveSameSchedule(
                            option.value,
                            data.end_time
                          );
                        }) || null
                      }
                      options={HOUR_OPTIONS.map((option) => {
                        if (!data.start_time) {
                          return option;
                        }
                        const diff = differenceInMinutes(
                          data.start_time,
                          option.value
                        );
                        return { ...option, isDisabled: diff >= 0 };
                      })}
                      onChange={(e) => {
                        if (!e) return;
                        setData((state) => ({
                          ...state,
                          end_time: e.value,
                        }));
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Checkbox
                      label="Não limitar horário"
                      checked={data.limit_hour}
                      onChange={(e) => {
                        setData((state) => ({
                          ...state,
                          start_time: null,
                          end_time: null,
                          limit_hour: !state.limit_hour,
                        }));
                      }}
                    />
                  </div>
                </HourRange>

                <div style={{ gridArea: 'limitCoupon' }}>
                  <Input
                    placeholder=""
                    disabled={limitCoupon}
                    onChange={(e) => {
                      e.persist();
                      setData((prevData) => ({
                        ...prevData,
                        userLimit: e.target.value,
                      }));
                    }}
                  />
                  <Checkbox
                    label="Limitar uso do cupom por cliente"
                    onChange={changeLimitCoupon}
                    containerStyles={{ marginTop: 6 }}
                  />
                </div>

                <div style={{ gridArea: 'limitClientCouponUse' }}>
                  <Input
                    placeholder="Digite o limite máx"
                    disabled={limitClientCouponUse}
                    onChange={(e) => {
                      e.persist();
                      setData((prevData) => ({
                        ...prevData,
                        limit_amount: e.target.value,
                      }));
                    }}
                  />
                  <Checkbox
                    label="Limitar utilização do cupom"
                    onChange={changeClienteCouponUse}
                    containerStyles={{ marginTop: 6 }}
                  />
                </div>
              </InputArea>

              <Row style={{ marginTop: 16 }}>
                <Col style={{}}>
                  <Switch
                    label="Cupom publico"
                    name="is_public"
                    checked={isPublic}
                    onChange={changeIsPublic}
                  />
                </Col>
              </Row>

              <Row
                style={{
                  marginTop: 16,
                  paddingBottom: 16,
                  borderBottom: '1px solid #EDEDED',
                }}
              >
                <Col style={{}}>
                  <Switch
                    label="Ativo"
                    name="active"
                    checked={active}
                    onChange={changeActive}
                  />
                </Col>
              </Row>

              <div
                style={{
                  marginTop: 16,
                  float: 'right',
                  display: 'flex',
                  gap: 8,
                }}
              >
                <Button
                  customColor="#C8131B"
                  children="Cancelar"
                  onClick={toggleAddTicketModal}
                />
                <Button customColor="#2EC9B7" children="Salvar" type="submit" />
              </div>
            </StyledForm>
          </Col>
        </Row>
      </Container>
    </ModalBody>
  );
}

export default NewTicket;
